import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, X, ChevronLeft, ChevronRight, FolderOpen } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "../components/ui/dialog";
import { portfolioData } from "../data/portfolioData";

const useGallery = () => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const openProject = (project) => {
    setSelectedProject(project);
    setCurrentImageIndex(0);
  };

  const closeProject = () => {
    setSelectedProject(null);
    setCurrentImageIndex(0);
  };

  const goToPrevious = () => {
    if (!selectedProject) return;
    setCurrentImageIndex(prev => 
      prev === 0 ? selectedProject.images.length - 1 : prev - 1
    );
  };

  const goToNext = () => {
    if (!selectedProject) return;
    setCurrentImageIndex(prev => 
      prev === selectedProject.images.length - 1 ? 0 : prev + 1
    );
  };

  const currentImage = selectedProject?.images[currentImageIndex] ?? null;
  const imageCount = selectedProject?.images.length ?? 0;
  const isBeforeImage = currentImage?.type === "before";
  const isAfterImage = currentImage?.type === "after";

  return {
    selectedProject, currentImageIndex, currentImage,
    imageCount, isBeforeImage, isAfterImage,
    openProject, closeProject, goToPrevious, goToNext, setCurrentImageIndex
  };
};

const Portfolio = () => {
  const {
    selectedProject, currentImageIndex, currentImage,
    imageCount, isBeforeImage, isAfterImage,
    openProject, closeProject, goToPrevious, goToNext, setCurrentImageIndex
  } = useGallery();

  return (
    <div data-testid="portfolio-page" className="bg-stone-50">
      {/* Intro Section */}
      <section className="py-24 bg-charcoal" data-testid="portfolio-intro">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="font-heading text-4xl sm:text-5xl lg:text-6xl text-stone-50 mb-6"
          >
            Our Portfolio
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="font-body text-base md:text-lg text-stone-300 max-w-2xl mx-auto"
          >
            Browse through our completed projects showcasing luxury bathrooms and residential refurbishments.
          </motion.p>
        </div>
      </section>

      {/* Bathrooms Section */}
      <section className="pb-16" data-testid="bathrooms-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-12"
          >
            <div className="flex items-center gap-4 mb-4">
              <div className="w-16 h-px bg-bronze" />
              <span className="font-body text-xs uppercase tracking-widest text-bronze">
                {portfolioData.bathrooms.title}
              </span>
              <div className="flex-1 h-px bg-stone-200" />
            </div>
          </motion.div>

          {/* Subsections */}
          {portfolioData.bathrooms.subsections.map((subsection, subsectionIndex) => (
            <div key={subsection.id} className="mb-20" data-testid={`subsection-${subsection.id}`}>
              {/* Subsection Header */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: subsectionIndex * 0.1 }}
                className="mb-10"
              >
                <h2 className="font-heading text-2xl md:text-3xl text-charcoal mb-2">
                  {subsection.title}
                </h2>
                <p className="font-body text-sm text-cool-grey">
                  {subsection.description}
                </p>
                {subsection.note && (
                  <p className="font-body text-sm text-cool-grey/80 mt-3 max-w-3xl leading-relaxed italic">
                    {subsection.note}
                  </p>
                )}
              </motion.div>

              {/* Project Folders Grid */}
              {subsection.projects.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {subsection.projects.map((project, projectIndex) => (
                    <motion.div
                      key={project.id}
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.6, delay: projectIndex * 0.1 }}
                      className="group cursor-pointer"
                      onClick={() => openProject(project)}
                      data-testid={`project-folder-${project.id}`}
                    >
                      {/* Folder Card */}
                      <div className="relative overflow-hidden rounded-sm bg-charcoal">
                        {/* Cover Image */}
                        <div className="aspect-[4/3] overflow-hidden">
                          <img
                            src={project.coverImage}
                            alt={project.title}
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/20 to-transparent opacity-60 group-hover:opacity-80 transition-opacity duration-500" />
                        </div>
                        
                        {/* Folder Icon & Info Overlay */}
                        <div className="absolute bottom-0 left-0 right-0 p-6">
                          <div className="flex items-center gap-2 mb-2">
                            <FolderOpen size={18} className="text-bronze" strokeWidth={1.5} />
                            <span className="font-body text-xs text-stone-50/70">
                              {project.images.length} images
                            </span>
                            {project.hasBeforeAfter && (
                              <span className="ml-2 px-2 py-0.5 bg-bronze/90 text-charcoal text-xs font-semibold rounded-full">
                                Before & After
                              </span>
                            )}
                          </div>
                          <h3 className="font-heading text-lg md:text-xl text-stone-50 leading-tight">
                            {project.title}
                          </h3>
                        </div>

                        {/* Hover Indicator */}
                        <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <div className="w-10 h-10 bg-bronze rounded-full flex items-center justify-center">
                            <ArrowRight size={18} className="text-charcoal" strokeWidth={2} />
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 bg-stone-100 rounded-sm">
                  <p className="font-body text-sm text-cool-grey">Projects coming soon</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Refurbishment Section */}
      <section className="pb-16" data-testid="refurbishment-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-12"
          >
            <div className="flex items-center gap-4 mb-4">
              <div className="w-16 h-px bg-bronze" />
              <span className="font-body text-xs uppercase tracking-widest text-bronze">
                {portfolioData.refurbishment.title}
              </span>
              <div className="flex-1 h-px bg-stone-200" />
            </div>
          </motion.div>

          {/* Subsections */}
          {portfolioData.refurbishment.subsections.map((subsection, subsectionIndex) => (
            <div key={subsection.id} className="mb-20" data-testid={`subsection-${subsection.id}`}>
              {/* Subsection Header */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: subsectionIndex * 0.1 }}
                className="mb-10"
              >
                <h2 className="font-heading text-2xl md:text-3xl text-charcoal mb-2">
                  {subsection.title}
                </h2>
                <p className="font-body text-sm text-cool-grey">
                  {subsection.description}
                </p>
              </motion.div>

              {/* Project Folders Grid */}
              {subsection.projects.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {subsection.projects.map((project, projectIndex) => (
                    <motion.div
                      key={project.id}
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.6, delay: projectIndex * 0.1 }}
                      className="group cursor-pointer"
                      onClick={() => openProject(project)}
                      data-testid={`project-folder-${project.id}`}
                    >
                      <div className="relative overflow-hidden rounded-sm bg-charcoal">
                        <div className="aspect-[4/3] overflow-hidden">
                          <img
                            src={project.coverImage}
                            alt={project.title}
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/20 to-transparent opacity-60 group-hover:opacity-80 transition-opacity duration-500" />
                        </div>
                        
                        <div className="absolute bottom-0 left-0 right-0 p-6">
                          <div className="flex items-center gap-2 mb-2">
                            <FolderOpen size={18} className="text-bronze" strokeWidth={1.5} />
                            <span className="font-body text-xs text-stone-50/70">
                              {project.images.length} images
                            </span>
                          </div>
                          <h3 className="font-heading text-lg md:text-xl text-stone-50 leading-tight">
                            {project.title}
                          </h3>
                        </div>

                        <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <div className="w-10 h-10 bg-bronze rounded-full flex items-center justify-center">
                            <ArrowRight size={18} className="text-charcoal" strokeWidth={2} />
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 bg-stone-100 rounded-sm">
                  <p className="font-body text-sm text-cool-grey">Projects coming soon</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-stone-100" data-testid="portfolio-cta">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="font-heading text-2xl md:text-3xl lg:text-4xl text-charcoal mb-8"
          >
            Ready to start your project?
          </motion.h2>
          <Link
            to="/contact"
            data-testid="portfolio-contact-btn"
            className="btn-primary inline-flex items-center gap-2"
          >
            Contact Us
            <ArrowRight size={16} strokeWidth={1.5} />
          </Link>
        </div>
      </section>

      {/* Project Gallery Modal */}
      <Dialog open={!!selectedProject} onOpenChange={() => closeProject()}>
        <DialogContent className="max-w-6xl w-[95vw] h-[90vh] p-0 bg-charcoal border-none overflow-hidden flex flex-col items-center">
          <DialogTitle className="sr-only">
            {selectedProject?.title || "Project Gallery"}
          </DialogTitle>
          
          {selectedProject && (
            <>
              {/* Close button */}
              <button
                onClick={closeProject}
                className="absolute top-4 right-4 z-50 p-2 bg-charcoal/80 rounded-full text-stone-50 hover:bg-charcoal transition-colors"
                data-testid="gallery-close-btn"
              >
                <X size={24} strokeWidth={1.5} />
              </button>

              {/* Navigation buttons */}
              {selectedProject.images.length > 1 && (
                <>
                  <button
                    onClick={goToPrevious}
                    className="absolute left-4 top-1/2 -translate-y-1/2 z-50 p-3 bg-charcoal/80 rounded-full text-stone-50 hover:bg-charcoal transition-colors"
                    data-testid="gallery-prev-btn"
                  >
                    <ChevronLeft size={28} strokeWidth={1.5} />
                  </button>
                  <button
                    onClick={goToNext}
                    className="absolute right-4 top-1/2 -translate-y-1/2 z-50 p-3 bg-charcoal/80 rounded-full text-stone-50 hover:bg-charcoal transition-colors"
                    data-testid="gallery-next-btn"
                  >
                    <ChevronRight size={28} strokeWidth={1.5} />
                  </button>
                </>
              )}

              {/* Main Image */}
              <div className="w-full h-full flex flex-col">
                {/* Project Title */}
                <div className="p-4 border-b border-stone-50/10 flex items-center justify-between">
                  <h3 className="font-heading text-xl text-stone-50">
                    {selectedProject.title}
                  </h3>
                  {selectedProject.hasBeforeAfter && currentImage?.type && (
                    <span className={`px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider ${
                      isBeforeImage ? 'bg-cool-grey text-white' : 'bg-bronze text-charcoal'
                    }`}>
                      {currentImage.type}
                    </span>
                  )}
                </div>

                {/* Image Display */}
                <div className="flex-1 flex items-center justify-center p-2 md:p-4 overflow-hidden">
                  <AnimatePresence mode="wait">
                    <motion.img
                      key={currentImageIndex}
                      src={currentImage.src}
                      alt={currentImage.caption}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="max-w-full max-h-[55vh] md:max-h-[65vh] object-contain rounded-sm mx-auto block"
                      data-testid="gallery-main-image"
                    />
                  </AnimatePresence>
                </div>

                {/* Caption & Counter */}
                <div className="p-4 border-t border-stone-50/10">
                  <p className="font-body text-sm text-stone-50/80 text-center mb-2">
                    {currentImage.caption}
                  </p>
                  <p className="font-body text-xs text-stone-50/50 text-center">
                    {currentImageIndex + 1} / {imageCount}
                  </p>
                </div>

                {/* Thumbnail Strip */}
                <div className="p-4 border-t border-stone-50/10">
                  <div className="flex gap-2 justify-center overflow-x-auto">
                    {selectedProject.images.map((image, index) => (
                      <button
                        key={`thumb-${image.src}`}
                        onClick={() => setCurrentImageIndex(index)}
                        className={`flex-shrink-0 w-16 h-16 rounded-sm overflow-hidden border-2 transition-all ${
                          index === currentImageIndex 
                            ? 'border-bronze' 
                            : 'border-transparent opacity-60 hover:opacity-100'
                        }`}
                        data-testid={`thumbnail-${index}`}
                      >
                        <img
                          src={image.src}
                          alt={`Thumbnail ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Portfolio;
